import type { FC, PropsWithChildren } from 'react';
import { Component, useMemo } from 'react';

import { AddContextualData } from '@opendoor/observability/slim';

import { TeamError, UnauthenticatedError, UnauthorizedError, UserError } from './Error';
import type { TEAM } from './teams';

export const OD_TEAM_BOUNDARY_ERROR = 'TeamErrorBoundary';

export interface ODTeamBoundaryProps extends PropsWithChildren {
  team: TEAM;
}

/**
 * Implements componentDidCatch to track the error in observability with team data but does not
 * render a fallback. That's still the responsibility of the app-wide ErrorBoundary.
 * @throws TeamError
 * @internal
 */
export class TeamErrorBoundary extends Component<
  ODTeamBoundaryProps,
  { hasError?: boolean; error?: Error }
> {
  /** Calls super */
  constructor(props: ODTeamBoundaryProps) {
    super(props);
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  /** @throws OD Team Boundary Error */
  override componentDidCatch(error: Error | TeamError) {
    // Append team to authorization and authentication errors and re-throw
    if (error instanceof UnauthenticatedError) {
      throw new UnauthenticatedError({
        triggeredQuery: error.triggeredQuery,
        from: 'app-code',
        team: this.props.team,
      });
    } else if (
      (error instanceof TeamError || error instanceof Error) &&
      error.message.includes('Attempted to query while unauthenticated')
    ) {
      throw new UnauthenticatedError({ from: 'app-code', team: this.props.team });
    } else if (error instanceof UnauthorizedError) {
      throw new UnauthorizedError(error.triggeredQuery, error.accessErrorType, this.props.team);
    }

    // Just forward information error has already been caught once by a TeamErrorBoundary
    if (error instanceof TeamError) {
      if (!error.team) {
        throw new TeamError(error, error.cause, this.props.team);
      }

      throw error;
    }

    if (error instanceof UserError) {
      throw new TeamError(error, 'user-error', this.props.team);
    }

    // Falls back to system error unless otherwise specified
    throw new TeamError(error, 'system-error', this.props.team);
  }

  override render() {
    return this.props.children;
  }
}

/**
 * ODTeamBoundary indicates that a section of the component tree belongs to a specific Opendoor team.
 * Errors that occur below this boundary -- both uncaught errors and errors tracked manually with the
 * trackError function from observability -- will be automatically tagged with the name of the team.
 */
export const ODTeamBoundary: FC<ODTeamBoundaryProps> = (props) => {
  const contextualData = useMemo(() => ({ team: props.team }), [props.team]);

  return (
    <AddContextualData data={contextualData}>
      <TeamErrorBoundary {...props} />
    </AddContextualData>
  );
};
